import { useCallback, useRef, useState } from 'react';

const MAX_HISTORY = 50;

interface UndoRedoState<T> {
  past: T[];
  present: T;
  future: T[];
}

export function useUndoRedo<T>(initialState: T) {
  const [state, setState] = useState<UndoRedoState<T>>({
    past: [],
    present: initialState,
    future: [],
  });

  const stateRef = useRef(state);
  stateRef.current = state;

  const set = useCallback((newPresent: T) => {
    setState((prev) => {
      if (newPresent === prev.present) return prev;
      const past = [...prev.past, prev.present].slice(-MAX_HISTORY);
      return { past, present: newPresent, future: [] };
    });
  }, []);

  const update = useCallback((updater: (current: T) => T) => {
    setState((prev) => {
      const newPresent = updater(prev.present);
      if (newPresent === prev.present) return prev;
      const past = [...prev.past, prev.present].slice(-MAX_HISTORY);
      return { past, present: newPresent, future: [] };
    });
  }, []);

  const undo = useCallback(() => {
    setState((prev) => {
      if (prev.past.length === 0) return prev;
      const previous = prev.past[prev.past.length - 1];
      const newPast = prev.past.slice(0, -1);
      return {
        past: newPast,
        present: previous,
        future: [prev.present, ...prev.future],
      };
    });
  }, []);

  const redo = useCallback(() => {
    setState((prev) => {
      if (prev.future.length === 0) return prev;
      const next = prev.future[0];
      const newFuture = prev.future.slice(1);
      return {
        past: [...prev.past, prev.present],
        present: next,
        future: newFuture,
      };
    });
  }, []);

  return {
    present: state.present,
    set,
    update,
    undo,
    redo,
    canUndo: state.past.length > 0,
    canRedo: state.future.length > 0,
  };
}
